import { useI18n } from "../lib/i18n";
import {
  PATHWAY_BLOCKS,
  WEEKLY_BLOCK_TOTAL,
  addBlock,
  blockTotal,
  removeBlock,
} from "../lib/pathways";
import type {
  CurriculumPathway,
  DeliveryMode,
  LearningRoute,
  SubjectBlocks,
} from "../types";

/**
 * How Al Noor will support one child, and what their week looks like.
 *
 * ── Why two routes and not three ────────────────────────────────────────────
 * "Subject Tutor Support" used to sit here as a third route, and it asked the
 * wrong question at the wrong moment: a family had to decide at enrolment
 * whether they would need a tutor — before teaching a single lesson, and
 * therefore before they could possibly know. The honest answer is almost always
 * "I will teach most of it and I do not yet know which part I will struggle
 * with", which was not one of the three options.
 *
 * Asking for a tutor is now an action on the curriculum screen, taken per
 * subject or per block when the need is actually felt. What is left here is the
 * one decision a family really can make on day one: am I teaching, or is a
 * teacher?
 *
 * ── Why the week is editable ────────────────────────────────────────────────
 * A pathway is a good starting point and a poor straitjacket. A family with a
 * child who needs more Urdu and less Science should be able to say so without
 * leaving the pathway, so blocks can be moved between subjects — and the total
 * is pinned at twenty, because a week nobody can timetable is not a plan.
 */

const ROUTES: LearningRoute[] = ["parent_led", "teacher_led"];

export const PATHWAY_IDS: CurriculumPathway[] = [
  "balanced",
  "pakistan",
  "cambridge",
  "faith",
  "future_skills",
];

const DELIVERIES: DeliveryMode[] = ["online", "home", "hybrid"];

export function EnrolmentChoice({
  route,
  pathway,
  earlyYears = false,
  delivery,
  blocks,
  onRoute,
  onPathway,
  onDelivery,
  onBlocks,
}: {
  route: LearningRoute | null;
  earlyYears?: boolean;
  pathway: CurriculumPathway | null;
  delivery: DeliveryMode | null;
  /** The family's allocation, or null while the pathway default is in force. */
  blocks: SubjectBlocks[] | null;
  onRoute: (value: LearningRoute) => void;
  onPathway: (value: CurriculumPathway) => void;
  onDelivery: (value: DeliveryMode) => void;
  onBlocks: (value: SubjectBlocks[] | null) => void;
}) {
  const { t, locale } = useI18n();
  const subjectLabel = (subject: string) => {
    if (locale !== "ur") return subject;
    const translated = t(`subject.${subject}`);
    return translated === `subject.${subject}` ? subject : translated;
  };
  const bullets = (prefix: string) => [1, 2, 3].map((index) => t(`${prefix}.b${index}`));

  // What the editor is working on: the family's own allocation once they have
  // touched it, the pathway's default until then.
  const current = blocks ?? (pathway ? PATHWAY_BLOCKS[pathway] : []);
  const used = blockTotal(current);

  return (
    <section className="enrolment-choice">
      <p className="form-help">{t("enrol.intro")}</p>
      <p className="section-rule">{t("enrol.routeQuestion")}</p>
      <div className="route-choice-grid two" role="group" aria-label={t("enrol.routeQuestion")}>
        {ROUTES.map((id) => (
          <button
            type="button"
            key={id}
            aria-pressed={route === id}
            className={route === id ? "route-choice selected" : "route-choice"}
            onClick={() => onRoute(id)}
          >
            <strong>{t(`enrol.route.${id}.title`)}</strong>
            <span>{t(`enrol.route.${id}.summary`)}</span>
            <span className="route-bullets">
              {bullets(`enrol.route.${id}`).map((line) => (
                <span key={line}>• {line}</span>
              ))}
            </span>
            <small>{t(`enrol.route.${id}.price`)}</small>
          </button>
        ))}
      </div>

      {route ? (
        <>
          {earlyYears ? <p className="form-help">{t("enrol.earlyYears")}</p> : null}
          <p className="section-rule">{t("enrol.pathwayQuestion")}</p>
          <div className="pathway-choice-grid" role="group" aria-label={t("enrol.pathwayQuestion")}>
            {PATHWAY_IDS.map((id) => (
              <button
                type="button"
                key={id}
                aria-pressed={pathway === id}
                className={pathway === id ? "pathway-choice selected" : "pathway-choice"}
                // Choosing a different pathway discards an edited week rather
                // than trying to carry it across: the subjects differ between
                // pathways, so a Cambridge allocation means nothing under
                // Faith-Integrated.
                onClick={() => {
                  onPathway(id);
                  onBlocks(null);
                }}
              >
                <strong>{earlyYears ? t(`enrol.earlyPathway.${id}.title`) : t(`enrol.pathway.${id}.title`)}</strong>
                <span>{earlyYears ? t(`enrol.earlyPathway.${id}.summary`) : t(`enrol.pathway.${id}.summary`)}</span>
                <small>
                  {earlyYears ? t("enrol.earlyFocus") : t("enrol.blocks")} · {t(`enrol.pathway.${id}.price`)}
                </small>
                {!earlyYears ? <span className="route-bullets">
                  {bullets(`enrol.pathway.${id}`).map((line) => (
                    <span key={line}>• {line}</span>
                  ))}
                </span> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {route && pathway && !earlyYears ? (
        <>
          <details className="block-editor">
            <summary>{t("enrol.blocksQuestion")}</summary>
            <p className="form-help">{t("enrol.blocksHelp", { total: WEEKLY_BLOCK_TOTAL })}</p>
            <ul>
              {current.map((line) => (
                <li key={line.subject}>
                  <span className="block-subject">{subjectLabel(line.subject)}</span>
                  <span className="block-bar" aria-hidden="true">
                    {Array.from({ length: line.blocks }).map((_, index) => (
                      <i key={index} />
                    ))}
                  </span>
                  <span className="block-controls">
                    <button
                      type="button"
                      aria-label={t("enrol.removeBlock", { subject: subjectLabel(line.subject) })}
                      disabled={line.blocks <= 1}
                      onClick={() => onBlocks(removeBlock(current, line.subject))}
                    >
                      −
                    </button>
                    <b>{line.blocks}</b>
                    <button
                      type="button"
                      aria-label={t("enrol.addBlock", { subject: subjectLabel(line.subject) })}
                      onClick={() => onBlocks(addBlock(current, line.subject))}
                    >
                      +
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <p className={used === WEEKLY_BLOCK_TOTAL ? "block-total" : "block-total off"}>
              {t("enrol.blocksTotal", { used, total: WEEKLY_BLOCK_TOTAL })}
              {blocks ? (
                <button type="button" className="link-button" onClick={() => onBlocks(null)}>
                  {t("enrol.blocksReset")}
                </button>
              ) : null}
            </p>
          </details>
        </>
      ) : null}

      {/*
        Delivery is a question about a teacher travelling to you, so it is only
        asked of the route that has one. A parent-led family who later requests
        a tutor is asked then, when it is about a real person and a real subject.
      */}
      {route === "teacher_led" ? (
        <>
          <p className="section-rule">{t("enrol.deliveryQuestion")}</p>
          <div className="evidence-options">
            {DELIVERIES.map((id) => (
              <button
                type="button"
                key={id}
                aria-pressed={delivery === id}
                className={delivery === id ? "selected" : ""}
                onClick={() => onDelivery(id)}
              >
                {t(`enrol.delivery.${id}.label`)}
                <small>{t(`enrol.delivery.${id}.hint`)}</small>
              </button>
            ))}
          </div>
          <div className="route-price-note">
            <strong>{t("enrol.priceNote.title")}</strong>
            <span>{t("enrol.priceNote.online")}</span>
            <span>{t("enrol.priceNote.home")}</span>
            <small>{t("enrol.priceNote.footnote")}</small>
          </div>
        </>
      ) : null}

      {/* Said here so a parent-led family knows help exists before they need it. */}
      {route === "parent_led" ? (
        <p className="form-help">{t("enrol.tutorLater")}</p>
      ) : null}
    </section>
  );
}
